import { Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { DateFormat } from '@app/constants';
import { DialogOptions, DialogService, FeedbackService } from '@app/core/services';
import { scrollToToolBar } from '@app/shared/forms/forms.helper';
import { UnitService } from '@app/shared/services';
import * as Transforms from '@app/utils/transforms';

@Injectable({
	providedIn: 'root',
})
export class AjsNgxModalService {
	constructor(
		private readonly dialogService: DialogService,
		private readonly unitService: UnitService,
		private readonly feedbackService: FeedbackService
	) {}

	/** showNgxModal
	 *
	 * @param title: string
	 * @param message: string
	 * @param okText: string
	 * @param cancelText: string
	 * @param showCancel: boolean
	 * @returns Observable<any>
	 * @description Show ngx modal from older AngularJS page.
	 */
	public showNgxModal(title: string, message: string, okText: string, cancelText: string, showCancel: boolean): Observable<any> {
		return this.dialogService.open({
			title,
			message,
			okText,
			cancelText,
			showCancel,
			disableClose: true,
		} as DialogOptions);
	}

	/** confirmCheckDateOutsideOfCurrentMonth
	 *
	 * @param checkDate: Date
	 * @param typeOfDate: string
	 * @returns Observable<any>
	 * @description Confirm check date outside of current month.
	 * Used by Process Payments etc.
	 */
	public confirmCheckDateOutsideOfCurrentMonth(checkDate: Date, typeOfDate = 'Check'): Observable<any> {
		const checkDateFormatted = Transforms.toDateFormat(checkDate, 'MM/DD/YYYY');
		const currentMY = `${new Date().getMonth()}${new Date().getFullYear()}`;
		const dateMY = `${checkDate.getMonth()}${checkDate.getFullYear()}`;

		if (currentMY === dateMY) {
			// return true, the dialog will not be shown
			return of(true);
		}

		return this.dialogService.open({
			title: `Confirm ${typeOfDate} Date Outside of Current Month`,
			message: `The ${typeOfDate} Date ${checkDateFormatted} is outside of the current month. Are you sure you want to continue?`,
			okText: 'YES',
			cancelText: 'NO',
			disableClose: true,
		} as DialogOptions);
	}

	/** isDateWithinCreatedFiscalYear
	 *
	 * @param effectiveDate: Date
	 * @param showFeedbackServiceErrorIfApplicable: boolean
	 * @param typeOfDate: string
	 * @returns Observable<boolean>
	 * @description Check if date is within created fiscal year.
	 */
	public isDateWithinCreatedFiscalYear(
		effectiveDate: Date,
		showFeedbackServiceErrorIfApplicable: boolean,
		typeOfDate = 'Check'
	): Observable<boolean> {
		const dateFormatted = Transforms.toDateFormat(effectiveDate, DateFormat.shortDate);
		return this.unitService.getFiscalYears().pipe(
			switchMap((response) => {
				const { fiscalYears, startMonth } = response;
				const effectiveDateFY = this.getFiscalYearFromDate(effectiveDate, startMonth);
				const dateIsWithinCreatedFiscalYear = fiscalYears.includes(effectiveDateFY);
				if (showFeedbackServiceErrorIfApplicable && !dateIsWithinCreatedFiscalYear) {
					this.feedbackService.error(
						`Error: ${typeOfDate} Date ${dateFormatted} requires fiscal year ${effectiveDateFY} to be created within System Settings!`
					);
					scrollToToolBar();
				}
				return dateIsWithinCreatedFiscalYear ? of(true) : of(false);
			})
		);
	}

	/** isDateOutsideFiscalYear
	 *
	 * @param effectiveDate: Date
	 * @param showFeedbackServiceErrorIfApplicable: boolean
	 * @param typeOfDate: string
	 * @returns Observable<boolean>
	 * @description Check if date is outside fiscal year.
	 */
	public isDateOutsideFiscalYear(effectiveDate: Date, showFeedbackServiceErrorIfApplicable: boolean, typeOfDate = 'Check'): Observable<boolean> {
		const dateFormatted = Transforms.toDateFormat(effectiveDate, 'MM/DD/YYYY');
		return this.unitService.getFiscalYears().pipe(
			switchMap((response) => {
				const { startMonth, currentFiscalYear } = response;
				const effectiveDateFY = this.getFiscalYearFromDate(effectiveDate, startMonth);
				const dateIsOutsideFiscalYear = currentFiscalYear !== effectiveDateFY;
				if (showFeedbackServiceErrorIfApplicable && dateIsOutsideFiscalYear) {
					this.feedbackService.error(`Error: ${typeOfDate} Date ${dateFormatted} is not within the current fiscal year!`);
					scrollToToolBar();
				}
				return dateIsOutsideFiscalYear ? of(true) : of(false);
			})
		);
	}

	private getFiscalYearFromDate(effectiveDate: Date, startMonth: string): number {
		const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

		const monthIndex = monthNames.indexOf(startMonth);
		let effectiveDateFY = effectiveDate.getFullYear();
		if (monthIndex > 1 && effectiveDate.getMonth() >= monthIndex) {
			effectiveDateFY++;
		}

		return effectiveDateFY;
	}
}
